/**
 * ChapterEditor.tsx — 章节编辑器（全屏覆盖层，books 域）· 已接真
 *
 * 移植自原型 design/Novel-admin/src/dialogs.jsx 838–981（注册名 chapter）；保留全屏三栏布局/样式。
 * 接真（ADM-BOK 运营编辑）：
 * - 正文：打开即 GET /api/v1/books/chapters/:id/content 回填；未发布章保存直接更新，已发布章保存为 pending draft，
 *   线上 snapshot 不动。重发布携 base snapshot + draft revision 双 CAS。
 *   读取异常（曾落正文却 R2 缺失 → 503）时禁编辑/禁存，避免从假空基线覆盖真实正文。
 * - 标题：PATCH /api/v1/books/chapters/:id（部分更新，不动正文；正文指针不在该 schema）。
 * - 价格(cost) / 免费(is_free)：**只读派生**，服务端按 dev-docs `03-数据字典/20-内容.md` §3.0 从字数 +
 *   该书千字单价 + 免费章数算出，本端点不收这两个字段（误传 VALIDATION_FAILED）。
 * - 未发布章「立即发布」走首发状态机；已发布章「重新发布」原子提升草稿。
 * 部署前提：正文桶为 C 端 app 读正文的同一内容桶 CONTENT_BUCKET（见 chapter-content-admin-service invariant）。
 */

import type { GetChapterContentResponse } from '@novel-admin/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { BookRow, ChapterRow } from '~/data/mock/types';
import { chaptersApi } from '~/hooks/use-books';
import { bookCodeDisplay, bookRowCodeText } from '~/lib/book-identity';
import { buildChapterMetadataPatch } from '~/lib/chapter-metadata-patch';
import { fmtDateTime, fmtLang, fmtNum } from '~/lib/format';
import { Icons } from '~/lib/icons';
import {
  INHERIT_DESC,
  VISIBILITY_META,
  visibilityChipClass,
  visibilityLabel,
} from '~/lib/novel-visibility-display';
import { toast } from '~/ui/Toast';
import { ChapterLeaveConfirm } from './ChapterLeaveConfirm';

const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';

/** 右侧栏键值对原子 — 移植自原型 dialogs.jsx Stat */
function Stat({ k, v, last }: { k: string; v: ReactNode; last?: boolean }) {
  return (
    <div
      className="hstack"
      style={{
        justifyContent: 'space-between',
        // 逐 CSS 规则对齐设计稿 dialogs.jsx:899（padding 5px 0 / fontSize 12 / 虚线分隔）
        padding: '5px 0',
        borderBottom: last ? 'none' : '1px dashed var(--border)',
        fontSize: 12,
      }}
    >
      <span style={{ color: 'var(--fg-muted)' }}>{k}</span>
      <span>{v}</span>
    </div>
  );
}

export function ChapterEditor({
  chapter,
  book,
  novelId,
  lang,
  canWrite,
  siblings,
  freeChapters,
  nextIndex,
  initialPublishMode,
  initialPublishBlockedReason,
  onClose,
}: {
  // null = 新建章节态（运营手动撰写）；非 null = 编辑既有章节
  chapter: ChapterRow | null;
  book: BookRow;
  novelId: string;
  lang: string;
  canWrite: boolean;
  siblings: ChapterRow[];
  // 当前 lang 的真实千字单价；价格公式 Stat 按 dev-docs ADM-APP-S007（千字 floor + 超千字百字阶梯）算参考价。缺则调用方传 schema 默认 30
  // 当前 lang 的免费章节数（前 N 章免费，接真 free_chapter_count）；行未就绪 = null → 显「—」
  freeChapters: number | null;
  // 新建态的章节序号（由调用方按 max(siblings.no)+1 算；编辑态忽略）
  nextIndex: number;
  // 未发布章首发必须复用书级计划真值；计划未知/缺时刻时禁发，绝不默认成立即发布。
  initialPublishMode: 'immediate' | 'scheduled' | 'blocked';
  initialPublishBlockedReason?: string;
  onClose: () => void;
}) {
  const isNew = chapter === null;
  const baseNo = chapter?.no ?? nextIndex;
  const baseStatus = chapter?.status ?? '草稿';
  const published = chapter?.publishedAt != null;

  const queryClient = useQueryClient();
  const [title, setTitle] = useState(chapter?.title ?? '');
  // 编辑值与 CAS token 必须来自同一份 mount-time fresh snapshot；不能直接跟随会后台刷新的 Query data。
  const [editorSnapshot, setEditorSnapshot] = useState<GetChapterContentResponse | null>(null);
  // 正文本地编辑值：null = 未编辑（编辑态用服务端加载值作基线；新建态基线为空串）
  const [bodyDraft, setBodyDraft] = useState<string | null>(null);
  // 正文域随内容自增高（设计稿意图：textarea 仅 minHeight、外层 .main editor 滚动）。原生 textarea 不会
  // 随内容长高 → 长正文被卡在 minHeight 框内出现内部小滚动条（与设计稿不符）。故把高度撑到 scrollHeight，
  // minHeight:400 作下限、由外层容器统一滚动，1:1 还原文档式编辑体验。
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);

  // 正文回填：GET /books/chapters/:id/content（新建态无 id → 跳过，基线空串）
  const contentQuery = useQuery({
    queryKey: ['chapter-content', chapter?.id],
    queryFn: () => chaptersApi.getContent(chapter?.id ?? ''),
    enabled: !isNew,
    staleTime: 0,
  });
  const loadedBaseTitle = isNew ? '' : (editorSnapshot?.title ?? chapter.title);
  // §3.0 两列纯派生：编辑器只展示服务端算好的值（§9.6 读模型按草稿覆盖后的 word_count 现算），
  // 不参与 dirty 判定、不随本地输入变化。新建章保存前服务端尚未派生 → 无值可展示。
  const derivedIsFree = isNew ? null : (editorSnapshot?.is_free ?? chapter.isFree);
  const derivedCost = isNew ? null : (editorSnapshot?.cost ?? String(chapter.price));
  useEffect(() => {
    // 命中无效缓存时 useQuery 会先给旧 data、同时 isFetching=true；必须等本次 mount 的网络刷新完成，
    // 再一次性冻结字段值 + CAS token，避免“运营看到旧值、保存却拿新 token”静默覆盖并发草稿。
    if (
      isNew ||
      editorSnapshot ||
      !contentQuery.isSuccess ||
      contentQuery.isFetching ||
      !contentQuery.data
    )
      return;
    setEditorSnapshot(contentQuery.data);
    setTitle(contentQuery.data.title);
  }, [contentQuery.data, contentQuery.isFetching, contentQuery.isSuccess, editorSnapshot, isNew]);

  const loadedBody = isNew ? '' : (editorSnapshot?.content ?? '');
  const body = bodyDraft ?? loadedBody;
  const bodyDirty = bodyDraft !== null && bodyDraft !== loadedBody;
  // 正文加载/编辑后把 textarea 高度撑到内容高度（见 bodyRef 注释）。useLayoutEffect 在 paint 前同步设高，
  // 避免长正文打开瞬间先现 minHeight 框再跳高的闪烁。
  // biome-ignore lint/correctness/useExhaustiveDependencies: body 是「内容变更→重算高度」的触发源；回调读 DOM 不直接引它，去掉则正文异步加载完后不会重算高度
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [body]);

  const words = body.replace(/\s/g, '').length;
  const titleValid = title.trim().length > 0;
  // 新建态：content 契约 min(1) → 正文必须非空才能创建
  const bodyNonEmpty = body.trim().length > 0;
  // §9.4 起本端点可改字段只剩 title——派生的 cost / is_free 不构成 dirty。
  const metaDirty = title !== loadedBaseTitle;
  const dirty = isNew ? titleValid || bodyNonEmpty || metaDirty : metaDirty || bodyDirty;
  const hasPendingDraft = !isNew && (editorSnapshot?.has_pending_draft ?? chapter.hasPendingDraft);
  const willCreatePendingDraft = published && dirty;
  const contentLoading = !isNew && !editorSnapshot && !contentQuery.isError;
  // 正文读取异常（getContent 抛 503：曾落正文却 R2 缺失）→ 禁存，避免从假空基线覆盖真实正文（adversarial r2#2）
  const contentLoadError = !isNew && !editorSnapshot && contentQuery.isError;
  const contentReady = isNew || editorSnapshot !== null;
  const editorFieldsDisabled = !canWrite || !contentReady;
  const canSave = isNew
    ? canWrite && titleValid && bodyNonEmpty
    : canWrite && contentReady && titleValid && dirty && !contentLoading && !contentLoadError;

  async function readVerifiedDraftSnapshot(
    id: string,
    expected: {
      baseSnapshotVersion: number;
      content: string;
      title: string;
    },
  ): Promise<GetChapterContentResponse> {
    const fresh = await chaptersApi.getContent(id);
    if (
      !fresh.has_pending_draft ||
      fresh.base_snapshot_version !== expected.baseSnapshotVersion ||
      fresh.content !== expected.content ||
      fresh.title !== expected.title
      // cost / is_free 不进对照：两列由服务端按 §3.0 从本次提交的 word_count 现算，
      // 提交前的值与保存后必然不同（正文改了字数就会变价），拿它当期望会把每次正常保存
      // 都误判成「被其他运营更新」。
    ) {
      throw new Error('章节草稿在保存期间被其他运营更新，请重新打开后再试');
    }
    return fresh;
  }

  function adoptVerifiedSnapshot(
    id: string,
    snapshot: GetChapterContentResponse,
    options: {
      submittedBody?: string;
      submittedMetadata?: { title: string };
    } = {},
  ) {
    queryClient.setQueryData(['chapter-content', id], snapshot);
    setEditorSnapshot(snapshot);
    if (options.submittedBody !== undefined) {
      // 请求在途期间字段仍可继续编辑；只清掉本次实际提交的值，晚于提交的新输入必须原样保留。
      setBodyDraft((current) => (current === options.submittedBody ? null : current));
    }
    if (options.submittedMetadata) {
      const submitted = options.submittedMetadata;
      setTitle((current) => (current.trim() === submitted.title ? snapshot.title : current));
    }
  }

  // ChapterResponse.content_version 始终是线上正文版本，不是独立草稿 revision。已发布章每次 SAVE 后
  // 都重新读 draft-aware 快照，并校验完整可见值仍等于“原基线 + 本次修改”后才采用新 revision；
  // 既能连续正文→元数据→重发布，也不会把运营未审阅过的并发草稿绑定到自己的发布动作。
  async function persist(): Promise<{
    id: string;
    draft?: { baseSnapshotVersion: number; draftContentVersion: number };
  }> {
    if (isNew) {
      const created = await chaptersApi.createWithContent(novelId, {
        chapter_index: nextIndex,
        title: title.trim(),
        content: body,
      });
      return { id: created.id };
    }
    if (!editorSnapshot) throw new Error('章节最新快照尚未加载，请稍候再试');
    const id = chapter.id;
    const expectedBaseSnapshotVersion =
      editorSnapshot.base_snapshot_version ??
      chapter.baseSnapshotVersion ??
      chapter.snapshotVersion;
    let draftRevision = editorSnapshot.content_version;
    let draftExists = hasPendingDraft;

    if (bodyDirty) {
      await chaptersApi.setContent(
        id,
        body,
        draftRevision,
        published ? expectedBaseSnapshotVersion : undefined,
      );
      if (published) {
        const fresh = await readVerifiedDraftSnapshot(id, {
          baseSnapshotVersion: expectedBaseSnapshotVersion,
          content: body,
          title: loadedBaseTitle,
        });
        draftRevision = fresh.content_version;
        draftExists = fresh.has_pending_draft;
        // 正文请求已经提交成功：立刻采用服务端验证过的新 revision/body 基线。后续元数据请求即使
        // 暂时失败，重试也只补 PATCH，不会拿旧 token 重复 PUT；尚未保存的元数据编辑值保持不变。
        adoptVerifiedSnapshot(id, fresh, { submittedBody: body });
      }
    }

    if (metaDirty) {
      // 只发真正改动的元数据字段：避免并发改同一章不同字段时用加载时的旧值覆盖对方改动。
      const patch = buildChapterMetadataPatch({ title: loadedBaseTitle }, { title: title.trim() });
      // 空补丁 → 跳过 PATCH（server 拒空 body）
      if (Object.keys(patch).length > 0) {
        if (published) {
          patch.expected_base_snapshot_version = expectedBaseSnapshotVersion;
          if (draftExists) patch.expected_draft_content_version = draftRevision;
        }
        await chaptersApi.update(id, patch);
        if (published) {
          const fresh = await readVerifiedDraftSnapshot(id, {
            baseSnapshotVersion: expectedBaseSnapshotVersion,
            content: body,
            title: title.trim(),
          });
          draftRevision = fresh.content_version;
          draftExists = fresh.has_pending_draft;
          adoptVerifiedSnapshot(id, fresh, {
            submittedMetadata: { title: title.trim() },
          });
        }
      }
    }
    return {
      id,
      draft:
        published && draftExists
          ? {
              baseSnapshotVersion: expectedBaseSnapshotVersion,
              draftContentVersion: draftRevision,
            }
          : undefined,
    };
  }
  async function invalidate() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['chapters', novelId] }),
      queryClient.invalidateQueries({ queryKey: ['publish-queue', novelId] }),
      queryClient.invalidateQueries({ queryKey: ['books'] }),
      queryClient.invalidateQueries({ queryKey: ['book-logs', novelId] }),
      ...(isNew
        ? []
        : [queryClient.invalidateQueries({ queryKey: ['chapter-content', chapter.id] })]),
    ]);
  }

  const saveDraft = useMutation({
    mutationFn: persist,
    onSuccess: async () => {
      toast(
        isNew ? '章节已创建（草稿）' : published ? '草稿已保存 · 线上版本不受影响' : '章节已保存',
        { tone: 'pos', icon: 'IconCheck' },
      );
      await invalidate();
      onClose();
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '保存失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  // 首发：立即模式同步发布，定时模式进入书级队列。重发布：提升 pending draft，携双 CAS。
  const publishNow = useMutation({
    mutationFn: async () => {
      const saved = await persist();
      if (!published) {
        return { mode: 'initial' as const, result: await chaptersApi.publish(saved.id) };
      }
      if (!saved.draft) {
        throw new Error('当前没有可重新发布的草稿');
      }
      return {
        mode: 'republish' as const,
        result: await chaptersApi.republish(saved.id, {
          expected_base_snapshot_version: saved.draft.baseSnapshotVersion,
          expected_draft_content_version: saved.draft.draftContentVersion,
        }),
      };
    },
    onSuccess: async (outcome) => {
      const message =
        outcome.mode === 'republish'
          ? '草稿已重新发布'
          : 'published_chapter_ids' in outcome.result
            ? `${outcome.result.published_chapter_ids.length} 章已同步发布`
            : '已加入发布队列';
      toast(message, { tone: 'pos', icon: 'IconCheck' });
      await invalidate();
      onClose();
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '发布失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  const rebaseDraft = useMutation({
    mutationFn: async () => {
      const current = editorSnapshot;
      if (!chapter || !current?.has_pending_draft) throw new Error('当前没有可 rebase 的草稿');
      return chaptersApi.rebaseDraft(chapter.id, {
        expected_draft_content_version: current.content_version,
      });
    },
    onSuccess: async () => {
      toast('草稿基线已更新 · 当前草稿内容保持不变', { tone: 'pos', icon: 'IconCheck' });
      setBodyDraft(null);
      await invalidate();
      if (chapter) {
        const fresh = await chaptersApi.getContent(chapter.id);
        queryClient.setQueryData(['chapter-content', chapter.id], fresh);
        setEditorSnapshot(fresh);
        setTitle(fresh.title);
      }
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '草稿 rebase 失败', {
        tone: 'neg',
        icon: 'IconAlert',
      }),
  });

  const pending = saveDraft.isPending || publishNow.isPending || rebaseDraft.isPending;
  const attemptClose = () => {
    if (!dirty || pending) {
      onClose();
      return;
    }
    setLeaveOpen(true);
  };
  // ESC 触发离开拦截（与编辑书籍一致）
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        attemptClose();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  // 前后章节导航（真实 siblings，按 no 排序取当前 ±2）；新建态 curIdx=末尾 → 展示最后几章作上下文
  const ordered = [...siblings].sort((a, b) => a.no - b.no);
  const curIdx = isNew ? ordered.length : ordered.findIndex((c) => c.id === chapter.id);
  const navRows = ordered.slice(Math.max(0, curIdx - 2), curIdx + 3);

  // pending 草稿的来源真值在 draft-aware GET；无草稿时才展示线上章节行的修改人/时刻。
  const lastSavedAt = hasPendingDraft
    ? fmtDateTime(editorSnapshot?.draft_updated_at ?? '')
    : chapter
      ? fmtDateTime(chapter.updatedAt)
      : '';
  const lastEditedByName = hasPendingDraft
    ? (editorSnapshot?.draft_last_edited_by_name ?? null)
    : (chapter?.lastEditedByName ?? null);
  // 章节进度接真：分子 = siblings 已发布数、分母 = siblings 总数（per-locale 全量列表，口径自洽，不再写死 N/N）
  const publishedCount = siblings.filter((c) => c.status === '已发布').length;
  const totalCount = siblings.length;
  // 字数已知：新建态(空正文真实=0) 或 正文已加载完（非 loading/非 error）。加载中/读取失败时 body='' → words=0 是假值，
  // 依赖 words 的展示（字数 meta / 字数 Stat / 价格公式）须降级显「—」，不误示「0 字 · ceil(0/1000)×单价」（对抗审查 F4）。
  const wordsKnown = isNew || contentReady;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '10px 18px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-elev)',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexShrink: 0,
        }}
      >
        <button className="btn btn-ghost btn-sm" onClick={attemptClose}>
          <Icons.IconChevLeft size={12} />
          返回章节列表
        </button>
        <div className="vdivider" style={{ height: 20 }} />
        <Icons.IconBook size={14} style={{ color: 'var(--fg-muted)' }} />
        <span style={{ fontSize: 12.5, fontWeight: 500 }}>
          《{book.displayTitle}》 ·{' '}
          <span className="mono muted">
            {bookCodeDisplay({
              bookCode: bookRowCodeText(book),
              bookGroupCode: book.bookGroupCode,
              locale: lang,
            })}
          </span>{' '}
          · {fmtLang(lang)} 版本
        </span>
        <span
          className={`chip ${baseStatus === '已发布' ? 'chip-pos' : 'chip-info'}`}
          style={{ fontSize: 10 }}
        >
          ● {isNew ? '新建' : baseStatus}
        </span>
        {hasPendingDraft && (
          <span className="chip chip-warn" style={{ fontSize: 10 }}>
            <Icons.IconEdit size={10} />
            草稿待重发布
          </span>
        )}
        {dirty && (
          <span className="chip chip-warn" style={{ fontSize: 10 }}>
            未保存
          </span>
        )}
        {willCreatePendingDraft && (
          <span
            className="chip chip-warn"
            style={{ fontSize: 10 }}
            title="保存仅写 pending draft，客户端仍读取当前线上 snapshot"
          >
            <Icons.IconAlert size={10} />
            保存后形成待重发布草稿 · 线上版本不受影响
          </span>
        )}
        <div style={{ flex: 1 }} />
        {/* 保存状态文本（1:1 对齐设计稿 dialogs.jsx:791）：dirty=「有未保存修改」/ 新建态=「未保存」/ 否则「已保存 · 时刻」接真 */}
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
          {dirty ? '有未保存修改' : isNew ? '未保存' : lastSavedAt ? `已保存 · ${lastSavedAt}` : ''}
        </span>
        {dirty && !pending && (
          <button
            className="btn"
            onClick={() => {
              setTitle(loadedBaseTitle);
              setBodyDraft(null);
              toast('已放弃修改', { tone: 'warn' });
            }}
          >
            放弃修改
          </button>
        )}
        <button
          className="btn"
          disabled={!canSave || pending}
          title={canWrite ? undefined : FORBIDDEN_ROLE_TITLE}
          onClick={() => saveDraft.mutate()}
        >
          {saveDraft.isPending ? '保存中…' : '保存草稿'}
        </button>
        {hasPendingDraft && (
          <button
            className="btn"
            disabled={!canWrite || dirty || pending}
            title={
              !canWrite
                ? FORBIDDEN_ROLE_TITLE
                : dirty
                  ? '请先保存或放弃本地修改，再 rebase 草稿'
                  : '保留当前草稿内容，并确认以最新线上快照作为发布基线'
            }
            onClick={() => rebaseDraft.mutate()}
          >
            {rebaseDraft.isPending ? '更新中…' : '更新草稿基线'}
          </button>
        )}
        <button
          className="btn btn-accent"
          disabled={
            !canWrite ||
            !titleValid ||
            (isNew && !bodyNonEmpty) ||
            contentLoading ||
            contentLoadError ||
            pending ||
            (!published && initialPublishMode === 'blocked') ||
            (published && !dirty && !hasPendingDraft)
          }
          title={
            !canWrite
              ? FORBIDDEN_ROLE_TITLE
              : !published && initialPublishMode === 'blocked'
                ? initialPublishBlockedReason
                : !published && initialPublishMode === 'scheduled'
                  ? '定时发布已开启，提交后进入书籍级队列，不会立即对客户端可见'
                  : undefined
          }
          onClick={() => publishNow.mutate()}
        >
          <Icons.IconCheck size={11} />
          {publishNow.isPending
            ? '发布中…'
            : published
              ? '重新发布'
              : initialPublishMode === 'scheduled'
                ? '加入发布队列'
                : '立即发布'}
        </button>
        <button className="btn btn-ghost btn-icon" onClick={attemptClose}>
          <Icons.IconX size={14} />
        </button>
      </div>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* main editor */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            padding: '32px 60px 60px',
          }}
        >
          <div style={{ width: '100%', maxWidth: 720 }}>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={editorFieldsDisabled}
              placeholder="章节标题"
              style={{
                width: '100%',
                border: 0,
                background: 'transparent',
                fontFamily: 'var(--font-display)',
                fontSize: 28,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: 'var(--fg-strong)',
                outline: 'none',
                marginBottom: 14,
              }}
            />
            <div
              className="hstack"
              style={{ gap: 8, marginBottom: 24, fontSize: 11.5, color: 'var(--fg-muted)' }}
            >
              <span className="mono">第 {String(baseNo).padStart(3, '0')} 章</span>
              <span>·</span>
              <span className="mono">{wordsKnown ? `${fmtNum(words)} 字` : '— 字'}</span>
              <span>·</span>
              <span className="mono">
                {derivedCost === null
                  ? '价格待派生'
                  : derivedIsFree
                    ? '免费'
                    : `${derivedCost} COIN`}
              </span>
              {/* pending 草稿展示 draft-aware GET 的编辑人/时刻；无草稿才展示线上章节来源。
                  viewer 的跨 admin 身份由服务端恒置 null，操作人未知时只显更新时间。 */}
              {!isNew && lastSavedAt && (
                <>
                  <span>·</span>
                  <span>
                    {lastEditedByName
                      ? `最后编辑 by ${lastEditedByName} · 更新于 ${lastSavedAt}`
                      : `更新于 ${lastSavedAt}`}
                  </span>
                </>
              )}
            </div>
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBodyDraft(e.target.value)}
              // 读取异常时也禁编辑（不止 loading）：曾落正文却读不到，从空白起编会覆盖真实正文（adversarial r2#2）
              disabled={editorFieldsDisabled}
              placeholder={
                contentLoading
                  ? '加载正文…'
                  : contentLoadError
                    ? '正文加载失败，请关闭重试'
                    : '输入章节正文…'
              }
              style={{
                width: '100%',
                border: 0,
                background: 'transparent',
                fontFamily: "'Geist', 'Noto Sans SC', sans-serif",
                fontSize: 15,
                lineHeight: 1.85,
                color: 'var(--fg)',
                outline: 'none',
                resize: 'none',
                overflow: 'hidden',
                minHeight: 400,
              }}
            />
          </div>
        </div>

        {/* sidebar */}
        <div
          style={{
            width: 280,
            borderLeft: '1px solid var(--border)',
            background: 'var(--bg-subtle)',
            padding: 14,
            overflow: 'auto',
            flexShrink: 0,
          }}
        >
          <div className="label" style={{ marginBottom: 6 }}>
            书籍信息
          </div>
          <div className="card" style={{ padding: 12, marginBottom: 14 }}>
            <div className="hstack" style={{ gap: 10, marginBottom: 10 }}>
              <div
                style={{
                  width: 40,
                  height: 54,
                  borderRadius: 4,
                  flexShrink: 0,
                  background: `linear-gradient(160deg, oklch(0.55 0.12 ${book.coverHue ?? 50}), oklch(0.32 0.08 ${(book.coverHue ?? 50) - 20}))`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                <Icons.IconBook size={15} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--fg-strong)' }}>
                  {book.displayTitle}
                </div>
                <div
                  className="mono"
                  style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 2 }}
                >
                  {bookCodeDisplay({
                    bookCode: bookRowCodeText(book),
                    bookGroupCode: book.bookGroupCode,
                    locale: lang,
                  })}
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 1 }}>
                  {book.author}
                </div>
              </div>
            </div>
            <Stat k="语言版本" v={<span className="mono">{fmtLang(lang)}</span>} />
            <Stat
              k="连载状态"
              v={
                <span
                  className={`chip ${book.serial === '已完结' ? 'chip-pos' : 'chip-info'}`}
                  style={{ fontSize: 10 }}
                >
                  {book.serial}
                </span>
              }
            />
            {/* 受众档（层② 覆盖层① 的合成值，A6 §15.1）；标签 / 色一律取展示模块唯一口径。
                未覆盖的行额外标「继承」—— 章节编辑器里也要看得出这档是本行配的还是跟着书组走。 */}
            <Stat
              k="受众档"
              v={
                <span className="hstack" style={{ gap: 4 }}>
                  <span
                    className={`chip ${visibilityChipClass(book.visibility)}`}
                    style={{ fontSize: 10 }}
                    title={VISIBILITY_META[book.visibility].desc}
                  >
                    {visibilityLabel(book.visibility)}
                  </span>
                  {!book.visibilityOverridden && (
                    <span
                      className="chip"
                      style={{ fontSize: 9, opacity: 0.7 }}
                      title={INHERIT_DESC}
                    >
                      继承
                    </span>
                  )}
                </span>
              }
            />
            <Stat
              k="章节进度"
              v={<span className="mono">{`${publishedCount} / ${totalCount}`}</span>}
            />
            <Stat
              k="免费章节"
              v={
                <span className="mono">{freeChapters != null ? `前 ${freeChapters} 章` : '—'}</span>
              }
              last
            />
          </div>

          <div className="label" style={{ marginBottom: 6 }}>
            章节属性
          </div>
          <div className="card" style={{ padding: 12, marginBottom: 14 }}>
            <Stat k="字数" v={<span className="mono">{wordsKnown ? fmtNum(words) : '—'}</span>} />
            {/* 计费 / 价格：`03-数据字典/20-内容.md` §3.0 纯派生列，**运营不可编辑**——价格由字数 +
                该书千字单价派生，免费与否由「前 N 章免费」∪「派生价为 0 的短章」并集决定。
                要改价改正文长度或该书千字单价（书籍编辑 §16.2），要改免费边界改免费章数。
                新建章保存前服务端尚未派生 → 显「保存后派生」。 */}
            <Stat
              k="计费"
              v={
                derivedIsFree === null ? (
                  <span className="mono" style={{ color: 'var(--fg-muted)' }}>
                    保存后派生
                  </span>
                ) : (
                  <span
                    className={`chip ${derivedIsFree ? 'chip-pos' : 'chip-info'}`}
                    style={{ fontSize: 10 }}
                  >
                    {derivedIsFree ? '免费' : '付费'}
                  </span>
                )
              }
            />
            <Stat
              k="价格(COIN)"
              v={
                <span
                  className="mono"
                  title="服务端按字数与该书千字单价派生（dev-docs 03-数据字典/20-内容.md §3.0）：不足 400 字免费，400 字起按百字阶梯计价"
                >
                  {derivedCost ?? '保存后派生'}
                </span>
              }
            />
            <Stat
              k="状态"
              v={
                <span
                  className={`chip ${baseStatus === '已发布' ? 'chip-pos' : 'chip-info'}`}
                  style={{ fontSize: 10 }}
                >
                  {isNew ? '新建' : baseStatus}
                </span>
              }
            />
          </div>

          <div className="label" style={{ marginBottom: 6 }}>
            前后章节
          </div>
          <div className="card" style={{ padding: 8 }}>
            {navRows.map((c) => {
              const isCur = c.id === chapter?.id;
              const chipClass =
                c.status === '已发布'
                  ? 'chip-pos'
                  : c.status === '草稿'
                    ? 'chip-info'
                    : 'chip-warn';
              return (
                <div
                  key={c.id}
                  className="hstack"
                  style={{
                    padding: '6px 6px',
                    gap: 8,
                    fontSize: 11.5,
                    borderRadius: 5,
                    background: isCur ? 'var(--accent-soft)' : 'transparent',
                    color: isCur ? 'var(--accent-fg)' : 'var(--fg-muted)',
                    fontWeight: isCur ? 500 : 400,
                  }}
                >
                  {isCur ? (
                    <Icons.IconEdit size={11} style={{ flexShrink: 0, color: 'var(--accent)' }} />
                  ) : c.no < baseNo ? (
                    <Icons.IconChevUp size={11} style={{ flexShrink: 0 }} />
                  ) : (
                    <Icons.IconChevDown size={11} style={{ flexShrink: 0 }} />
                  )}
                  <span className="mono" style={{ flexShrink: 0 }}>
                    {String(c.no).padStart(3, '0')}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.title}
                  </span>
                  <span className={`chip ${chipClass}`} style={{ fontSize: 9.5 }}>
                    {isCur ? '当前' : c.status}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {leaveOpen && (
        <ChapterLeaveConfirm
          onSave={() => {
            setLeaveOpen(false);
            saveDraft.mutate();
          }}
          onDiscard={() => {
            setLeaveOpen(false);
            onClose();
          }}
          onCancel={() => setLeaveOpen(false)}
          canSave={canSave}
        />
      )}
    </div>,
    document.body,
  );
}
